import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Box,
  IconButton,
  IconButtonProps,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation } from '@tanstack/react-query';
import {
  IRuleset,
  IConditionNode,
  getConditionChildren,
  isConditionGroup,
} from '../../types/ruleset-types';
import { useUpdateRuleset } from './hooks';
import { AppContext } from '../../App';
import { standardTableStyles } from '../common/tableStyles';
import useGlynt from '../hooks/useGlynt';

interface RulesetListProps {
  rulesets: IRuleset[];
  dataPoolId: string;
  count: number;
  page: number;
  perPage: number;
  onEdit: (ruleset: IRuleset) => void;
  onCopy: (ruleset: IRuleset) => void;
  onDelete: (ruleset: IRuleset) => void;
  onRefresh: () => void;
}

const countConditions = (node: IConditionNode): number => {
  if (!node) return 0;
  if (isConditionGroup(node)) {
    return getConditionChildren(node).reduce((acc, child) => acc + countConditions(child), 0);
  }
  return 1;
};

const sortRulesets = (items: IRuleset[]) => {
  return [...items].sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    return a.id.localeCompare(b.id);
  });
};

const reorderList = (items: IRuleset[], startIndex: number, endIndex: number) => {
  const next = [...items];
  const [removed] = next.splice(startIndex, 1);
  next.splice(endIndex, 0, removed);
  return next;
};

const RulesetList: React.FC<RulesetListProps> = ({
  rulesets,
  dataPoolId,
  count,
  page,
  perPage,
  onEdit,
  onCopy,
  onDelete,
  onRefresh,
}) => {
  const { setToast } = useContext(AppContext);
  const glynt = useGlynt();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [orderedRulesets, setOrderedRulesets] = useState<IRuleset[]>(() => sortRulesets(rulesets));

  useEffect(() => {
    setOrderedRulesets(sortRulesets(rulesets));
  }, [rulesets]);

  const { mutate: updateRuleset, isPending } = useUpdateRuleset({
    onSuccess: () => {
      onRefresh();
      setToast({
        variant: 'success',
        open: true,
        message: 'Ruleset status updated',
      });
    },
    onError: (error) => {
      setToast({
        variant: 'error',
        open: true,
        message: error?.message || 'Failed to update ruleset status',
      });
    },
  });
  const reorderMutation = useMutation({
    mutationFn: async (updates: Array<{ id: string; priority: number }>) => {
      for (const update of updates) {
        await (await glynt).ruleset.update(dataPoolId, update.id, { priority: update.priority });
      }
    },
  });

  const handleToggleActive = (ruleset: IRuleset) => {
    updateRuleset({
      dataPoolId,
      rulesetId: ruleset.id,
      data: { is_active: !ruleset.is_active },
    });
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || reorderMutation.isPending) {
      return;
    }

    const { source, destination } = result;
    if (source.index === destination.index) {
      return;
    }

    const previousRulesets = orderedRulesets;
    const previousPriorities = new Map(previousRulesets.map((ruleset) => [ruleset.id, ruleset.priority]));
    const priorityOffset = page * perPage;
    const reordered = reorderList(orderedRulesets, source.index, destination.index).map((ruleset, index) => ({
      ...ruleset,
      priority: (priorityOffset + index) * 10,
    }));
    const updates = reordered
      .filter((ruleset) => ruleset.priority !== previousPriorities.get(ruleset.id))
      .map((ruleset) => ({
        id: ruleset.id,
        priority: ruleset.priority,
      }));

    if (updates.length === 0) {
      return;
    }

    setOrderedRulesets(reordered);

    try {
      await reorderMutation.mutateAsync(updates);
      onRefresh();
      setToast({
        variant: 'success',
        open: true,
        message: 'Ruleset priority updated',
      });
    } catch (error: any) {
      setOrderedRulesets(previousRulesets);
      setToast({
        variant: 'error',
        open: true,
        message: error?.message || 'Failed to reorder rulesets',
      });
    }
  };

  if (count === 0) {
    return (
      <Box
        sx={{
          py: 8,
          textAlign: 'center',
          backgroundColor: 'grey.50',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
        }}
        data-cy="rulesets-empty-state"
      >
        <Typography variant="body1" color="text.secondary" fontWeight={500}>
          No rulesets configured
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
          Create a ruleset to define document routing rules.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={standardTableStyles.containerSx}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="rulesets-table-body">
          {(droppableProvided, droppableSnapshot) => (
            <Table sx={standardTableStyles.tableSx}>
              <TableHead sx={standardTableStyles.tableHeadSx}>
                <TableRow>
                  <TableCell sx={{ width: 52 }} />
                  <TableCell>Label</TableCell>
                  <TableCell align="center">Priority</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Conditions</TableCell>
                  <TableCell align="center">Actions</TableCell>
                  <TableCell align="center">Updated</TableCell>
                  <TableCell align="right">Operations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                sx={{
                  ...standardTableStyles.tableBodySx,
                  backgroundColor: droppableSnapshot.isDraggingOver ? '#F7FBFC' : 'transparent',
                }}
              >
                {orderedRulesets.map((ruleset, index) => (
                  <Draggable
                    key={ruleset.id}
                    draggableId={ruleset.id}
                    index={index}
                    isDragDisabled={isPending || reorderMutation.isPending}
                    disableInteractiveElementBlocking
                  >
                    {(draggableProvided, draggableSnapshot) => (
                      <TableRow
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        sx={{
                          opacity: ruleset.is_active ? 1 : 0.5,
                          transition: 'opacity 0.2s',
                          backgroundColor: draggableSnapshot.isDragging ? '#F1F6F8' : 'transparent',
                          boxShadow: draggableSnapshot.isDragging
                            ? '0 10px 24px rgba(17, 34, 51, 0.12)'
                            : 'none',
                          '& > *': {
                            backgroundColor: draggableSnapshot.isDragging ? '#F1F6F8' : undefined,
                          },
                        }}
                        data-cy={`ruleset-row-${ruleset.label}`}
                      >
                        <TableCell align="center" sx={{ width: 52 }}>
                          <Tooltip title="Drag to reorder priority">
                            <span>
                              <IconButton
                                size="small"
                                {...draggableProvided.dragHandleProps}
                                disabled={isPending || reorderMutation.isPending}
                                sx={{
                                  cursor: isPending || reorderMutation.isPending ? 'default' : 'grab',
                                  color: draggableSnapshot.isDragging ? 'primary.main' : 'text.disabled',
                                }}
                                data-cy={`drag-ruleset-${ruleset.label}`}
                              >
                                <DragIndicatorIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {ruleset.label}
                            </Typography>
                            {ruleset.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {ruleset.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontFamily: 'monospace',
                              fontSize: '13px',
                              color: 'text.secondary',
                            }}
                          >
                            {ruleset.priority}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={ruleset.is_active ? 'Click to deactivate' : 'Click to activate'}>
                            <Switch
                              checked={ruleset.is_active}
                              onChange={() => handleToggleActive(ruleset)}
                              disabled={isPending || reorderMutation.isPending}
                              size="small"
                              data-cy={`switch-ruleset-active-${ruleset.label}`}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {countConditions(ruleset.conditions)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {ruleset.actions.length}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {new Date(ruleset.updated_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Tooltip title="Edit ruleset">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => onEdit(ruleset)}
                                  disabled={reorderMutation.isPending}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main' },
                                  }}
                                  data-cy={`btn-edit-ruleset-${ruleset.label}`}
                                >
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Copy as new">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => onCopy(ruleset)}
                                  disabled={reorderMutation.isPending}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main' },
                                  }}
                                  data-cy={`btn-copy-ruleset-${ruleset.label}`}
                                >
                                  <FileCopyIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete ruleset">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => onDelete(ruleset)}
                                  disabled={reorderMutation.isPending}
                                  sx={{
                                    color: 'text.disabled',
                                    '&:hover': {
                                      color: 'error.main',
                                      backgroundColor: 'error.lighter',
                                    },
                                  }}
                                  data-cy={`btn-delete-ruleset-${ruleset.label}`}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </TableBody>
            </Table>
          )}
        </Droppable>
      </DragDropContext>
      {count > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 15, 25, 50]}
          component="div"
          count={count}
          rowsPerPage={perPage}
          page={page}
          backIconButtonProps={
            {
              'aria-label': 'Previous Page',
              'data-cy': 'btn-prev-page',
            } as IconButtonProps & { 'data-cy': string }
          }
          nextIconButtonProps={
            {
              'aria-label': 'Next Page',
              'data-cy': 'btn-next-page',
            } as IconButtonProps & { 'data-cy': string }
          }
          onPageChange={(e, p) => {
            e?.preventDefault();
            const params = new URLSearchParams(search);
            params.set('page', p.toString());
            navigate({ search: params.toString() });
          }}
          onRowsPerPageChange={(e) => {
            const params = new URLSearchParams(search);
            params.set('perPage', e.target.value);
            params.set('page', '0');
            navigate({ search: params.toString() });
          }}
        />
      )}
    </Box>
  );
};

export default RulesetList;
